import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtInt, clamp, now } from '@/utils/format';
import { useFpsMeter } from '@/hooks/useFpsMeter';
import { qualityFromFps, scoreFromFps } from '@/utils/scoring';
import {
  mat4Identity, mat4Multiply, mat4Perspective, mat4Translate,
  mat4RotateY, mat4RotateX, mat4LookAt,
  createProgram, buildCubeGeometry, createCheckerTexture,
} from '@/utils/webgl-helpers';

interface Props { autoRun: boolean; autoNonce: number; }

export function WebGLTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [scene, setScene] = useState<'simple' | 'complex'>('complex');
  const [cubeCount, setCubeCount] = useState(140);
  const [particleCount, setParticleCount] = useState(2500);
  const [supported, setSupported] = useState(false);
  const [webgl2, setWebgl2] = useState(false);
  const [warning, setWarning] = useState('');
  const [drawCallsPerFrame, setDrawCallsPerFrame] = useState(0);
  const [frameMs, setFrameMs] = useState(0);

  const fps = useFpsMeter(running);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const resRef = useRef<Record<string, unknown> | null>(null);
  const autoDoneRef = useRef(false);
  const runningRef = useRef(false);

  function resizeGLCanvas(canvas: HTMLCanvasElement) {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    return { w, h };
  }

  function doReport(currentFps: number) {
    const sc = Math.round(scoreFromFps(currentFps));
    const testId: TestId = 'tv-webgl';
    reportResult(testId, {
      score: sc,
      primary: `${webgl2 ? 'WebGL2' : 'WebGL1'} · FPS ${currentFps ? currentFps.toFixed(1) : '—'}`,
      details: { supported, webgl2, fps: currentFps, drawCallsPerFrame, frameMs },
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  function stopInternal(isForced: boolean) {
    runningRef.current = false;
    setRunning(false);
    cancelAnimationFrame(rafRef.current);
    glRef.current = null;
    resRef.current = null;
    if (!isForced) doReport(fps);
  }

  function start() {
    autoDoneRef.current = false;
    setSupported(false); setWebgl2(false); setWarning('');

    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl2 = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, powerPreference: 'high-performance' }) as WebGLRenderingContext | null;
    const gl = gl2 || canvas.getContext('webgl', { antialias: false, alpha: false, depth: true, powerPreference: 'high-performance' });
    if (!gl) {
      setWarning('WebGL not supported');
      reportResult('tv-webgl', { score: 0, primary: 'Not supported', details: { supported: false } });
      if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone('tv-webgl'); }
      return;
    }

    glRef.current = gl;
    const is2 = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
    setSupported(true);
    setWebgl2(is2);
    if (!is2) setWarning('WebGL2 not available');

    // Shaders
    const vsTexLit = `
      attribute vec3 aPos; attribute vec3 aNor; attribute vec2 aUV;
      uniform mat4 uModel; uniform mat4 uViewProj;
      varying vec3 vNor; varying vec2 vUV;
      void main(){ vNor=mat3(uModel)*aNor; vUV=aUV; gl_Position=uViewProj*uModel*vec4(aPos,1.0); }`;
    const fsTexLit = `
      precision mediump float;
      varying vec3 vNor; varying vec2 vUV;
      uniform sampler2D uTex; uniform vec3 uLightDir;
      void main(){ vec3 n=normalize(vNor); float d=max(dot(n,normalize(uLightDir)),0.0);
        vec3 a=texture2D(uTex,vUV*2.0).rgb; gl_FragColor=vec4(a*(0.25+d*0.95),1.0); }`;
    const vsPts = `
      attribute vec3 aPos; uniform mat4 uViewProj; uniform float uSize;
      void main(){ gl_Position=uViewProj*vec4(aPos,1.0); gl_PointSize=uSize; }`;
    const fsPts = `
      precision mediump float;
      void main(){ vec2 p=gl_PointCoord.xy-vec2(0.5); float d=dot(p,p);
        float a=smoothstep(0.25,0.0,d); gl_FragColor=vec4(0.39,0.71,1.0,a); }`;

    let progTex: WebGLProgram, progPts: WebGLProgram;
    try {
      progTex = createProgram(gl, vsTexLit, fsTexLit);
      progPts = createProgram(gl, vsPts, fsPts);
    } catch (err) {
      setWarning(String(err));
      return;
    }

    const cube = buildCubeGeometry();
    const vbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, cube.verts, gl.STATIC_DRAW);
    const ibo = gl.createBuffer()!;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, cube.indices, gl.STATIC_DRAW);

    const maxPts = clamp(parseInt(String(particleCount), 10) || 2500, 200, 12000);
    const pts = new Float32Array(maxPts * 3);
    const vel = new Float32Array(maxPts * 3);
    let seed = 246813579;
    function rnd() { seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5; return (seed >>> 0) / 4294967295; }
    for (let i = 0; i < maxPts; i++) {
      pts[i * 3] = (rnd() - 0.5) * 1.8; pts[i * 3 + 1] = rnd() * 1.5; pts[i * 3 + 2] = (rnd() - 0.5) * 1.8;
      vel[i * 3] = (rnd() - 0.5) * 0.012; vel[i * 3 + 1] = 0.006 + rnd() * 0.018; vel[i * 3 + 2] = (rnd() - 0.5) * 0.012;
    }
    const vboPts = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, vboPts);
    gl.bufferData(gl.ARRAY_BUFFER, pts, gl.DYNAMIC_DRAW);

    const tex = createCheckerTexture(gl);
    gl.enable(gl.DEPTH_TEST);

    resRef.current = { progTex, progPts, vbo, ibo, vboPts, tex, cubeIndexCount: cube.indices.length, pts, vel, ptCount: maxPts, is2 };
    runningRef.current = true;
    setRunning(true);

    const lightDir = [0.6, 0.9, 0.2];
    let lastT = now();
    let emaFrame = 0;

    function loop(time: number) {
      if (!runningRef.current || !glRef.current || !resRef.current) return;
      const info = resizeGLCanvas(canvas!);
      gl!.viewport(0, 0, info.w, info.h);
      const dt = time - lastT || 16.7;
      lastT = time;
      emaFrame = emaFrame ? emaFrame * 0.85 + dt * 0.15 : dt;

      gl!.clearColor(0.02, 0.04, 0.06, 1);
      gl!.clear(gl!.COLOR_BUFFER_BIT | gl!.DEPTH_BUFFER_BIT);

      const aspect = info.w / info.h;
      const proj = mat4Perspective(1.05, aspect, 0.1, 60);
      const view = mat4LookAt([0, 1.6, 4.8], [0, 0.6, 0], [0, 1, 0]);
      const viewProj = mat4Multiply(view, proj);
      let dc = 0;
      const sec = time / 1000;
      const res = resRef.current as Record<string, unknown>;

      // Textured cubes
      gl!.useProgram(res.progTex as WebGLProgram);
      gl!.bindBuffer(gl!.ARRAY_BUFFER, res.vbo as WebGLBuffer);
      gl!.bindBuffer(gl!.ELEMENT_ARRAY_BUFFER, res.ibo as WebGLBuffer);

      const stride = 8 * 4;
      const aP = gl!.getAttribLocation(res.progTex as WebGLProgram, 'aPos');
      const aN = gl!.getAttribLocation(res.progTex as WebGLProgram, 'aNor');
      const aU = gl!.getAttribLocation(res.progTex as WebGLProgram, 'aUV');
      gl!.enableVertexAttribArray(aP); gl!.enableVertexAttribArray(aN); gl!.enableVertexAttribArray(aU);
      gl!.vertexAttribPointer(aP, 3, gl!.FLOAT, false, stride, 0);
      gl!.vertexAttribPointer(aN, 3, gl!.FLOAT, false, stride, 12);
      gl!.vertexAttribPointer(aU, 2, gl!.FLOAT, false, stride, 24);

      const uModel = gl!.getUniformLocation(res.progTex as WebGLProgram, 'uModel');
      const uVP = gl!.getUniformLocation(res.progTex as WebGLProgram, 'uViewProj');
      const uLight = gl!.getUniformLocation(res.progTex as WebGLProgram, 'uLightDir');
      const uTex = gl!.getUniformLocation(res.progTex as WebGLProgram, 'uTex');

      gl!.uniformMatrix4fv(uVP, false, new Float32Array(viewProj));
      gl!.uniform3fv(uLight, new Float32Array(lightDir));
      gl!.activeTexture(gl!.TEXTURE0);
      gl!.bindTexture(gl!.TEXTURE_2D, res.tex as WebGLTexture);
      gl!.uniform1i(uTex, 0);

      // Central cube
      let m = mat4Identity();
      m = mat4Translate(m, 0, 0.6, 0);
      m = mat4RotateY(m, sec * 1.2);
      m = mat4RotateX(m, sec * 0.7);
      gl!.uniformMatrix4fv(uModel, false, new Float32Array(m));
      gl!.drawElements(gl!.TRIANGLES, res.cubeIndexCount as number, gl!.UNSIGNED_SHORT, 0);
      dc++;

      if (scene === 'complex') {
        const nCubes = clamp(parseInt(String(cubeCount), 10) || 140, 20, 600);
        for (let i = 0; i < nCubes; i++) {
          const a = i * 0.22 + sec * 0.6;
          let m2 = mat4Identity();
          m2 = mat4Translate(m2, Math.cos(a) * (1.4 + (i % 7) * 0.08), 0.15 + (i % 9) * 0.07, Math.sin(a) * (1.4 + (i % 5) * 0.1));
          m2 = mat4RotateY(m2, a);
          gl!.uniformMatrix4fv(uModel, false, new Float32Array(m2));
          gl!.drawElements(gl!.TRIANGLES, res.cubeIndexCount as number, gl!.UNSIGNED_SHORT, 0);
          dc++;
        }
      }

      // Particles
      gl!.useProgram(res.progPts as WebGLProgram);
      gl!.bindBuffer(gl!.ARRAY_BUFFER, res.vboPts as WebGLBuffer);
      const aPos = gl!.getAttribLocation(res.progPts as WebGLProgram, 'aPos');
      gl!.enableVertexAttribArray(aPos);
      gl!.vertexAttribPointer(aPos, 3, gl!.FLOAT, false, 12, 0);
      gl!.uniformMatrix4fv(gl!.getUniformLocation(res.progPts as WebGLProgram, 'uViewProj'), false, new Float32Array(viewProj));
      gl!.uniform1f(gl!.getUniformLocation(res.progPts as WebGLProgram, 'uSize'), ((res.is2 as boolean) ? 3.0 : 2.0) * (window.devicePixelRatio || 1));

      const ptsArr = res.pts as Float32Array;
      const velArr = res.vel as Float32Array;
      const ptCount = res.ptCount as number;
      for (let i = 0; i < ptCount; i++) {
        const k = i * 3;
        ptsArr[k] += velArr[k]; ptsArr[k + 1] += velArr[k + 1]; ptsArr[k + 2] += velArr[k + 2];
        if (ptsArr[k + 1] > 2.6) { ptsArr[k] *= 0.2; ptsArr[k + 1] = 0; ptsArr[k + 2] *= 0.2; }
        velArr[k + 1] -= 0.00035;
        if (velArr[k + 1] < 0.004) velArr[k + 1] = 0.004 + (i % 9) * 0.00015;
      }
      gl!.bufferSubData(gl!.ARRAY_BUFFER, 0, ptsArr);
      gl!.enable(gl!.BLEND);
      gl!.blendFunc(gl!.SRC_ALPHA, gl!.ONE);
      gl!.drawArrays(gl!.POINTS, 0, ptCount);
      gl!.disable(gl!.BLEND);
      dc++;

      setDrawCallsPerFrame(dc);
      setFrameMs(emaFrame);

      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);
  }

  function reset() { stopInternal(true); setScene('complex'); setCubeCount(140); setParticleCount(2500); setWarning(''); }

  useEffect(() => {
    if (!autoRun) return;
    setScene('complex'); setCubeCount(140); setParticleCount(2500);
    start();
    const timer = setTimeout(() => stopInternal(false), 7000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const q = qualityFromFps(fps);

  return (
    <TestCard title={t('tv.webgl.title', language)} description={t('tv.webgl.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={() => stopInternal(false)} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.scene', language)}</div>
          <select value={scene} onChange={(e) => setScene(e.target.value as 'simple' | 'complex')} disabled={running || autoRun} className="input">
            <option value="simple">{t('opt.simple', language)}</option>
            <option value="complex">{t('opt.complex', language)}</option>
          </select>
        </div>
        <div className="field">
          <div className="label">{t('label.cubes', language)}</div>
          <input value={cubeCount} disabled={running || autoRun || scene === 'simple'} onChange={(e) => setCubeCount(Number(e.target.value))} inputMode="numeric" className="input w-20" />
        </div>
        <div className="field">
          <div className="label">{t('label.particles', language)}</div>
          <input value={particleCount} disabled={running || autoRun || scene === 'simple'} onChange={(e) => setParticleCount(Number(e.target.value))} inputMode="numeric" className="input w-24" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label="WebGL" value={supported ? (webgl2 ? 'WebGL2' : 'WebGL1') : 'NO'} hint={warning || '—'} />
        <MetricBox label={t('metric.fps', language)} value={running ? fps.toFixed(1) : '—'} hint={t('hint.fpsGood', language)} />
        <MetricBox label={t('metric.frameTime', language)} value={running ? frameMs.toFixed(1) + ' ms' : '—'} />
        <MetricBox label={t('metric.drawCalls', language)} value={running ? fmtInt(drawCallsPerFrame) : '—'} />
      </div>
      {warning && <><div className="h-px bg-border my-3.5" /><div className="text-xs text-yellow-300">{warning}</div></>}
      <div className="h-px bg-border my-3.5" />
      <canvas ref={canvasRef} className="test-canvas border border-border rounded-2xl bg-black/20" />
      <div className="text-xs mt-2"><span className={`font-black ${q.cls}`}>Quality: {running ? q.label : '—'}</span></div>
    </TestCard>
  );
}
